import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';
import { useDismissable } from '@hooks/useDismissable';
import field from './filterField.module.css';
import styles from './RangeFilter.module.css';

/** Matches `min-width` on the panel; used to decide which edge it hangs from. */
const PANEL_WIDTH = 232;

/**
 * The right edge the panel must stay inside.
 *
 * Not the viewport: the table card clips its own overflow, so a panel on one of
 * the last columns is cut off well before the window ends.
 */
function clippingRight(element: HTMLElement): number {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowX, overflow } = window.getComputedStyle(node);
    if (overflowX !== 'visible' || overflow !== 'visible') {
      return node.getBoundingClientRect().right;
    }
  }
  return window.innerWidth;
}

/** The two ends of an inclusive range, as the URL and the API carry them. */
export interface RangeBounds {
  from?: string;
  to?: string;
}

/** The "no value" choice a range filter can offer alongside its bounds (ADO-1118). */
export interface RangeEmptyFilter {
  /** Checkbox text, and the trigger's text once applied, e.g. "Blank (no date)". */
  label: string;
  /** Whether a blank filter is what the list is showing. */
  applied: boolean;
  onApply: () => void;
}

export interface RangeFilterProps {
  columnLabel: string;
  /** What the bound inputs accept: a native date picker, or free text. */
  inputType: 'date' | 'text';
  inputMode?: 'decimal';
  placeholder?: { from: string; to: string };
  /** Trigger text while no range is set, e.g. "Any date". */
  emptyLabel: string;
  value?: RangeBounds;
  /** Null when both bounds are empty. */
  onChange: (value: RangeBounds | null) => void;
  /** The problem with this pair in the user's words, or null when it is usable. */
  validate: (from: string, to: string) => string | null;
  /** How the set range reads on the closed trigger. */
  summarise: (from: string, to: string) => string;
  /** Offer "no value" too; absent on a column that can never be empty. */
  emptyFilter?: RangeEmptyFilter;
}

/**
 * A bounded range filter, as one narrow trigger plus a small panel.
 *
 * Both ends used to sit inline in the header cell. Two native date inputs are
 * ~240px wide together, so the filter — not the data — decided the column's
 * width, and at 1920px the Amount, Status and Reassessment columns were pushed
 * off screen behind a horizontal scroll. Collapsed to a trigger, the column is
 * back to the width its own content asks for, and the panel finally has room
 * for real labels, a validation message that shifts nothing, and Apply.
 *
 * Nothing is queried until Apply (or Enter). The previous control committed on
 * blur, which fired a query whenever you clicked away mid-thought and gave you
 * no way to say "not yet". A draft that differs from what the list is showing
 * marks the trigger as pending rather than applying itself quietly.
 */
const RangeFilter: React.FC<RangeFilterProps> = ({
  columnLabel,
  inputType,
  inputMode,
  placeholder,
  emptyLabel,
  value,
  onChange,
  validate,
  summarise,
  emptyFilter
}) => {
  const appliedFrom = value?.from ?? '';
  const appliedTo = value?.to ?? '';
  const appliedBlank = emptyFilter?.applied ?? false;

  const [isOpen, setIsOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [from, setFrom] = useState(appliedFrom);
  const [to, setTo] = useState(appliedTo);
  const [blankDraft, setBlankDraft] = useState(appliedBlank);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const blankBoxRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();

  // Follow the value when it changes elsewhere — Clear all, or a browser Back
  // that rewrites the URL. After our own Apply this is a no-op, because the
  // value that arrives is the draft we just sent.
  useEffect(() => {
    setFrom(appliedFrom);
    setTo(appliedTo);
    setBlankDraft(appliedBlank);
    setError(null);
  }, [appliedFrom, appliedTo, appliedBlank]);

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const revertAndClose = useCallback(() => {
    setFrom(appliedFrom);
    setTo(appliedTo);
    setBlankDraft(appliedBlank);
    setError(null);
    close();
  }, [appliedFrom, appliedTo, appliedBlank, close]);

  // Escape cancels, so the draft goes back to what the list is showing. A click
  // outside is not a cancel, so the draft survives and the trigger says so.
  useDismissable(containerRef, isOpen, (source) =>
    source === 'escape' ? revertAndClose() : setIsOpen(false)
  );

  // Opening should land the caret in the first bound, not on the trigger. The
  // last columns sit near the viewport edge, where a left-aligned panel would
  // be cut off by the table's own horizontal scroll.
  useEffect(() => {
    if (!isOpen) return;
    // With blank chosen the bounds are disabled and cannot take focus.
    (blankDraft ? blankBoxRef : firstInputRef).current?.focus();

    const trigger = triggerRef.current;
    if (trigger) {
      setAlignRight(trigger.getBoundingClientRect().left + PANEL_WIDTH > clippingRight(trigger));
    }
    // Only on opening: ticking blank later must not pull focus around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = from !== appliedFrom || to !== appliedTo || blankDraft !== appliedBlank;
  const hasApplied = Boolean(appliedFrom || appliedTo) || appliedBlank;

  const apply = () => {
    if (blankDraft && emptyFilter) {
      setError(null);
      emptyFilter.onApply();
      close();
      return;
    }

    const problem = validate(from, to);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onChange(!from && !to ? null : { ...(from ? { from } : {}), ...(to ? { to } : {}) });
    close();
  };

  const clear = () => {
    setFrom('');
    setTo('');
    setBlankDraft(false);
    setError(null);
    onChange(null);
    close();
  };

  const clearFromTrigger = (event: React.MouseEvent) => {
    event.stopPropagation();
    setFrom('');
    setTo('');
    setBlankDraft(false);
    setError(null);
    onChange(null);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      apply();
    }
  };

  const draftSummary = blankDraft && emptyFilter ? emptyFilter.label : summarise(from, to);
  const summary = isDirty || hasApplied ? draftSummary : '';
  const bound = (which: 'from' | 'to') => {
    const isFrom = which === 'from';
    return (
      <div className={styles.bound}>
        <label className={styles.boundLabel} htmlFor={`${fieldId}-${which}`}>
          {isFrom ? 'From' : 'To'}
        </label>
        <input
          ref={isFrom ? firstInputRef : undefined}
          id={`${fieldId}-${which}`}
          type={inputType}
          inputMode={inputMode}
          className={clsx(styles.boundInput, inputMode === 'decimal' && styles.numeric)}
          value={isFrom ? from : to}
          placeholder={isFrom ? placeholder?.from : placeholder?.to}
          max={inputType === 'date' && isFrom ? to || undefined : undefined}
          min={inputType === 'date' && !isFrom ? from || undefined : undefined}
          onChange={(event) => (isFrom ? setFrom : setTo)(event.target.value)}
          onKeyDown={onInputKeyDown}
          disabled={blankDraft}
          aria-label={`Filter ${columnLabel} ${which}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : undefined}
        />
      </div>
    );
  };

  return (
    <div className={styles.rangeFilter} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={clsx(
          field.field,
          styles.trigger,
          hasApplied && !isDirty && field.hasValue,
          isDirty && field.isPending
        )}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Filter by ${columnLabel}`}
        title={isDirty ? `${summary} — not applied yet` : summary || undefined}
      >
        <span className={clsx(field.summary, !summary && field.placeholder)}>
          {summary || emptyLabel}
        </span>
        {hasApplied && !isDirty ? (
          <span
            className={field.affordance}
            onClick={clearFromTrigger}
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${columnLabel} filter`}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className={styles.chevron} aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div
          className={clsx(field.panel, styles.panel, alignRight && field.panelRight)}
          role="dialog"
          aria-label={`${columnLabel} range`}
        >
          {emptyFilter && (
            <label className={styles.blankOption}>
              <input
                ref={blankBoxRef}
                type="checkbox"
                checked={blankDraft}
                onChange={(event) => {
                  setBlankDraft(event.target.checked);
                  setError(null);
                }}
                onKeyDown={onInputKeyDown}
              />
              <span>{emptyFilter.label}</span>
            </label>
          )}
          {bound('from')}
          {bound('to')}

          {error && (
            <p className={styles.error} id={`${fieldId}-error`} role="alert">
              {error}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.clear}
              onClick={clear}
              disabled={!hasApplied && !from && !to && !blankDraft}
            >
              Clear
            </button>
            <button type="button" className={styles.apply} onClick={apply} disabled={!isDirty}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeFilter;
